import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingColumns } from "@fortawesome/free-solid-svg-icons";
import Avatar from "../../../../../components/Avatar";
import Drawer from "../../../../../components/Drawer";
import Loading from "../../../../../components/Loaders/Loading";
import ColumnToggle from "../../../../../components/NTable/Actions/ColumnToggle";
import Item from "../../../components/Overtime/Table/SubContent/Content/Item";
import { useTable } from "../../../components/Overtime/hooks/useTable";
import { useSubTableColumns } from "../../../components/Overtime/hooks/useSubTableColumns";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useDateFilters } from "../../../hooks/useDateFilters";
import { useSortTimeSheets } from "../../../components/TimeSheetTable/hooks/useSortTimeSheets";
import { useCompanyCurrency, useHasCompany } from "../../../../../utils/hooks/company";
import { getTimeSheetTotal } from "../../../components/TimeSheetTable/useColumns/useTotalColumn";
import { getTimeSheetDuration } from "../../../components/TimeSheetTable/useColumns/useDurationColumn";
import { renderUserName } from "../../../../../utils/helpers/user";
import { renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../utils/helpers/currencyFormatter";
import "./style.scss";

function OvertimeDrawer({ open, close, values, isMyTimesheet }) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const hasCompany = useHasCompany();
    const [selectedProfile, setSelectedProfile] = useState(values?.id);
    const { from, to } = useDateFilters();
    const { groups } = useGroups();
    const columns = useSubTableColumns({ groupBy: true, overtimeDrawer: true });

    const userId = useMemo(() => {
        return [values?.id, ...(values?.otherProfileIds || [])]
    }, [values?.id, values?.otherProfileIds]);

    const { startDate, endDate } = useMemo(() => {
        let startDate = from, endDate = to;
        if (values?.date) {
            startDate = values.date;
            endDate = values.date;
        }

        return {
            startDate: moment(startDate).format("YYYY-MM-DD"),
            endDate: moment(endDate).format("YYYY-MM-DD")
        }
    }, [from, to, values.date]);

    const { url, params } = useMemo(() => {
        const url = isMyTimesheet
            ? "/time-sheet/multiple-profiles/my-profile-timesheets"
            : "/time-sheet/multiple-profiles/users";
        const params = {
            startDate,
            endDate,
            userId
        };

        return { url, params };
    }, [endDate, isMyTimesheet, startDate, userId]);

    const { data, isLoading } = useSWR(hasCompany ? {
        url,
        params
    } : null, { fallbackData: { data: { result: [], totalItems: 0 } } });

    const { profileUrl, profileParams } = useMemo(() => {
        const url = isMyTimesheet
            ? "/time-sheet/my-multiple-profiles"
            : "/time-sheet/multiple-profiles";
        const params = { startDate, endDate, page: 1, perPage: 5 };
        if (!isMyTimesheet) {
            params.employeeNumber = values?.employeeNumber;
        }
        return { profileUrl: url, profileParams: params };
    }, [endDate, isMyTimesheet, startDate, values?.employeeNumber]);

    const { data: profiles, isLoading: profilesLoading } = useSWR(hasCompany ? {
        url: profileUrl,
        params: profileParams
    } : null, { fallbackData: { data: { result: [], totalItems: 0 } } });

    const options = useMemo(() => {
        return userId.reduce((total, id) => {
            const item = profiles?.result?.find((item) => item.id === id);
            if (item) {
                const users = [item, ...(item?.otherProfiles || [])]
                total.push(...users);
            };
            return total;
        }, []);
    }, [userId, profiles?.result]);

    const userData = useMemo(() => {
        return data?.result?.filter((item) => item.user.id === selectedProfile);
    }, [data?.result, selectedProfile]);

    const table = useTable({
        data: useSortTimeSheets(userData || []),
        columns,
        totalItems: userData?.length || 0,
        initialState: {
            columnVisibility: {
                "payCode_hourType": false,
                "payCode_description": false,
                job: false,
                location: false,
                overnight: false
            }
        }
    });

    useEffect(() => {
        if (!groups?.length || groups?.length === 0) return;
        table.setColumnVisibility((prev) => {
            const columnVisibility = prev;
            groups.forEach((group) => {
                const value = `group_${group.level.replaceAll("_", "")}`;
                columnVisibility[value] = false;
            })
            return columnVisibility;
        })
    }, [groups, table]);

    const user = useMemo(() => {
        return options.find((option) => option.id === selectedProfile);
    }, [options, selectedProfile]);

    const title = useMemo(() => {
        return (
            <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                    <Avatar user={user || values} />
                    {renderUserName(user || values)}
                </div>
                <span className="text-gray font-weight-500">{"("}{t("overtimes")}{")"}</span>
            </div>
        )
    }, [user, values, t]);

    const { totalAmount, workedHours, overtimeHours } = useMemo(() => {
        const totalAmount = userData?.reduce((total, item) => {
            return total + getTimeSheetTotal(item);
        }, 0);

        const workedHours = userData
            ?.filter((item) => {
                if (item.hourType === "unpaid") return false;
                if (item.payCode.excludeFromPaidHours) return false;
                return item.hourType !== "overtime";
            })
            ?.reduce((total, item) => {
                return total + getTimeSheetDuration(item);
            }, 0);

        const overtimeHours = userData
            ?.filter((item) => item.hourType === "overtime")
            ?.reduce((total, item) => {
                return total + getTimeSheetDuration(item);
            }, 0);
        return { totalAmount, workedHours, overtimeHours };
    }, [userData]);

    return (
        <Drawer
            title={title}
            open={open}
            close={close}
            className="overtime-drawer"
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                    {options?.map((option, index) => (
                        <span
                            key={option.id}
                            className={classNames(
                                option.id === selectedProfile && "bg-info text-white border-info",
                                "cursor-pointer border px-3 py-2",
                                index === 0 && "rounded-left",
                                index === options.length - 1 && "rounded-right"
                            )}
                            onClick={() => setSelectedProfile(option.id)}
                        >
                            {option.employeeNumber}
                        </span>
                    ))}
                </div>
                <ColumnToggle columns={table.getAllColumns()} />
            </div>

            <div>
                {(isLoading || profilesLoading) ? (
                    <div className="d-flex align-items-center justify-content-center py-4">
                        <Loading />
                    </div>
                ) : (
                    <div>
                        <div
                            className="py-3 px-4 d-flex gap-2 align-items-center border rounded text-dark font-weight-bold"
                            style={{ background: "#f2eaf8" }}
                        >
                            <FontAwesomeIcon icon={faBuildingColumns} />  {user?.primaryCompany?.name || values?.primaryCompany?.name}
                        </div>
                        <Item table={table} footer={false} />
                    </div>
                )}
            </div>

            <div className="py-3 px-4 d-flex gap-2 align-items-center border rounded" style={{ background: "#f2eaf8" }}>
                <div className="d-flex align-items-center gap-2 mx-3 text-dark">
                    <h4 className="m-0 p-0 font-weight-bolder">{t("worked-hours")}:</h4>
                    <span>{renderDurationAsFormat(workedHours, "HH:mm")}</span>
                </div>

                <div className="d-flex align-items-center gap-2 mx-3 text-dark">
                    <h4 className="m-0 p-0 font-weight-bolder">{t("overtime-hours")}:</h4>
                    <span>{renderDurationAsFormat(overtimeHours, "HH:mm")}</span>
                </div>

                <div className="d-flex align-items-center gap-2 mx-3 text-dark">
                    <h4 className="m-0 p-0 font-weight-bolder">{t("total-amount")}:</h4>
                    <span>{currencyFormatter(totalAmount, 2, currency)}</span>
                </div>
            </div>
        </Drawer>
    );
}

export default OvertimeDrawer;
